/**
 * BookingDetailDialog (N3, Oct 2026) — full booking view: every stage
 * field, the lifecycle history trail, and exactly ONE next valid
 * action per status (DRAFT → confirm [reusing an attached source, or
 * linking one], then the strict
 * stage advances). The backend remains authoritative — these buttons
 * are UX shortcuts over the same gated endpoints.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { ArrowRight, Pencil, Trash2 } from "lucide-react";
import PermButton from "./PermButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_COLORS = {
  DRAFT: "bg-amber-100 text-amber-900 border-amber-300",
  CONFIRMED: "bg-blue-100 text-blue-900 border-blue-300",
  VEHICLE_REPORTED: "bg-indigo-100 text-indigo-900 border-indigo-300",
  DISPATCHED: "bg-violet-100 text-violet-900 border-violet-300",
  REPORTED: "bg-cyan-100 text-cyan-900 border-cyan-300",
  UNLOADED: "bg-emerald-100 text-emerald-900 border-emerald-300",
  DELETED: "bg-rose-100 text-rose-900 border-rose-300",
};

export const STATUS_LABELS = {
  DRAFT: "Draft",
  CONFIRMED: "Confirmed",
  VEHICLE_REPORTED: "Vehicle Reported",
  DISPATCHED: "Dispatched",
  REPORTED: "Reported",
  UNLOADED: "Unloaded",
  DELETED: "Deleted",
};

// The single next valid action per status (AC: "next valid action only").
const NEXT_ACTION = {
  DRAFT: { key: "confirm", label: "Link Source & Confirm" },
  CONFIRMED: { key: "vehicle-reported", label: "Mark Vehicle Reported" },
  VEHICLE_REPORTED: { key: "dispatch", label: "Mark Dispatched" },
  DISPATCHED: { key: "report", label: "Mark Reported" },
  REPORTED: { key: "unload", label: "Mark Unloaded" },
};

const SOURCE_TYPES = ["DC", "IBT", "DISPATCH_LOT", "DISPATCH_DRAFT"];

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="text-sm">{value ?? "—"}</div>
  </div>
);

export default function BookingDetailDialog({ open, onClose, bookingId, onChanged, onEdit, onDelete }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [actionForm, setActionForm] = useState({});

  const load = () => {
    if (!open || !bookingId) return;
    setLoading(true);
    setActionOpen(false);
    axios
      .get(`${API}/logistics-bookings/${bookingId}`)
      .then((res) => setBooking(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Could not load booking");
        onClose();
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [open, bookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSource = Boolean(booking?.source_type && booking?.source_id);

  const setA = (k) => (e) => setActionForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const runAction = async () => {
    const action = NEXT_ACTION[booking?.status];
    if (!action) return;
    setActing(true);
    try {
      let payload = {};
      if (action.key === "confirm") {
        const missing = [];
        if (!hasSource && (!actionForm.source_type || !actionForm.source_id)) missing.push("Source");
        if (!String(actionForm.transporter_name || "").trim()) missing.push("Transporter name");
        const cost = actionForm.booking_cost !== "" && actionForm.booking_cost != null
          ? Number(actionForm.booking_cost) : null;
        if (cost == null || !(cost > 0)) missing.push("Booking Cost (₹) > 0");
        if (!String(actionForm.vehicle_number || "").trim()) missing.push("Vehicle number");
        if (missing.length) {
          toast.error(`Required: ${missing.join(", ")}`);
          setActing(false);
          return;
        }
        // An already-attached source is reused server-side — not re-sent.
        payload = {
          ...(hasSource ? {} : { source_type: actionForm.source_type, source_id: actionForm.source_id }),
          transporter_name: actionForm.transporter_name.trim(),
          transporter_phone: actionForm.transporter_phone || null,
          booking_cost: cost,
          vehicle_number: actionForm.vehicle_number.trim(),
        };
      } else if (action.key === "vehicle-reported") {
        payload = { vehicle_number: actionForm.vehicle_number || null };
      } else if (action.key === "dispatch") {
        payload = { dispatch_date: actionForm.date || null };
      } else if (action.key === "report") {
        payload = { reported_date: actionForm.date || null };
      } else if (action.key === "unload") {
        payload = {
          unloaded_date: actionForm.date || null,
          additional_cost: actionForm.additional_cost || null,
        };
      }
      const res = await axios.post(
        `${API}/logistics-bookings/${booking.id}/${action.key}`, payload);
      toast.success(`${res.data.booking_code} → ${STATUS_LABELS[res.data.status]}`);
      setActionOpen(false);
      setActionForm({});
      load();
      onChanged && onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Action failed");
    } finally {
      setActing(false);
    }
  };

  const baseNext = booking ? NEXT_ACTION[booking.status] : null;
  const next = baseNext && baseNext.key === "confirm" && hasSource
    ? { ...baseNext, label: "Confirm Booking" } : baseNext;

  // Pre-fill the action form with values already on the booking.
  const openAction = () => {
    if (next?.key === "confirm") {
      setActionForm({
        transporter_name: booking.transporter_name || "",
        transporter_phone: booking.transporter_phone || "",
        booking_cost: booking.booking_cost ?? "",
        vehicle_number: booking.vehicle_number || "",
      });
    } else if (next?.key === "vehicle-reported") {
      setActionForm({ vehicle_number: booking.vehicle_number || "" });
    } else {
      setActionForm({});
    }
    setActionOpen(true);
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!acting && !o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="booking-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {booking?.booking_code || "Booking"}
            {booking && (
              <Badge className={`border ${STATUS_COLORS[booking.status]}`} data-testid="booking-detail-status">
                {STATUS_LABELS[booking.status] || booking.status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {booking?.source_type
              ? `Linked to ${booking.source_type} ${booking.source_id}`
              : "Standalone booking (no source linked)"}
          </DialogDescription>
        </DialogHeader>

        {loading || !booking ? (
          <div className="py-6 text-sm text-zinc-500" data-testid="booking-detail-loading">Loading…</div>
        ) : (
          <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-3" data-testid="booking-detail-fields">
              <Field label="Vehicle type" value={booking.vehicle_type ? `${booking.vehicle_type} ft` : null} />
              <Field label="From" value={booking.from_location} />
              <Field label="To" value={booking.to_location} />
              <Field label="Delivery location" value={booking.delivery_location} />
              <Field label="Appointment date" value={booking.appointment_date} />
              <Field label="Transporter" value={booking.transporter_name} />
              <Field label="Transporter phone" value={booking.transporter_phone} />
              <Field label="Booking Cost (₹)" value={booking.booking_cost ?? null} />
              <Field label="Vehicle number" value={booking.vehicle_number} />
              <Field label="Confirmation date" value={booking.confirmation_date} />
              <Field label="Dispatch date" value={booking.dispatch_date} />
              <Field label="Reported date" value={booking.reported_date} />
              <Field label="Unloaded date" value={booking.unloaded_date} />
              <Field label="Additional cost" value={booking.additional_cost} />
              <Field label="Geotag" value={booking.geotag ? `${booking.geotag.lat}, ${booking.geotag.long}` : null} />
              {booking.status === "DELETED" && (
                <>
                  <Field label="Pre-delete status" value={booking.pre_delete_status} />
                  <Field label="Deleted at" value={booking.deleted_at} />
                  <Field label="Deleted by" value={booking.deleted_by_name || booking.deleted_by} />
                </>
              )}
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Lifecycle history</div>
              <ol className="space-y-1" data-testid="booking-detail-history">
                {(booking.history || []).map((h, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" data-testid={`history-entry-${i}`}>
                    <Badge variant="outline" className="text-xs">{h.from || "—"}</Badge>
                    <ArrowRight className="h-3 w-3 text-zinc-400" />
                    <Badge className={`border text-xs ${STATUS_COLORS[h.to] || ""}`}>
                      {STATUS_LABELS[h.to] || h.to}
                    </Badge>
                    <span className="text-xs text-zinc-500">
                      {h.by_name || h.by} · {h.at ? new Date(h.at).toLocaleString() : ""}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {actionOpen && next && (
              <div className="rounded-md border p-3 space-y-3" data-testid="booking-detail-action-form">
                <div className="text-sm font-medium">{next.label}</div>
                {next.key === "confirm" && (
                  <div className="grid grid-cols-2 gap-3">
                    {hasSource ? (
                      <div className="col-span-2 text-sm text-zinc-600" data-testid="bd-confirm-linked-source">
                        Linked to {booking.source_type} {booking.source_id} — confirming against this source.
                      </div>
                    ) : (<>
                    <div>
                      <Label>Source type <span className="text-rose-600">*</span></Label>
                      <Select value={actionForm.source_type || ""} onValueChange={setA("source_type")}>
                        <SelectTrigger data-testid="bd-field-source_type">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCE_TYPES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Source id <span className="text-rose-600">*</span></Label>
                      <Input value={actionForm.source_id || ""} onChange={setA("source_id")} data-testid="bd-field-source_id" />
                    </div>
                    </>)}
                    <div>
                      <Label>Transporter name <span className="text-rose-600">*</span></Label>
                      <Input value={actionForm.transporter_name || ""} onChange={setA("transporter_name")} data-testid="bd-field-transporter_name" />
                    </div>
                    <div>
                      <Label>Transporter phone</Label>
                      <Input value={actionForm.transporter_phone || ""} onChange={setA("transporter_phone")} data-testid="bd-field-transporter_phone" />
                    </div>
                    <div>
                      <Label>Booking Cost (₹) <span className="text-rose-600">*</span></Label>
                      <Input type="number" value={actionForm.booking_cost ?? ""} onChange={setA("booking_cost")}
                        onWheel={(e) => e.currentTarget.blur()} className="no-spinner"
                        data-testid="bd-field-booking_cost" />
                    </div>
                    <div>
                      <Label>Vehicle number <span className="text-rose-600">*</span></Label>
                      <Input value={actionForm.vehicle_number || ""} onChange={setA("vehicle_number")} data-testid="bd-field-confirm_vehicle_number" />
                    </div>
                  </div>
                )}
                {next.key === "vehicle-reported" && (
                  <div>
                    <Label>Vehicle number (recorded at Confirm — change only to correct it)</Label>
                    <Input value={actionForm.vehicle_number || ""} onChange={setA("vehicle_number")} data-testid="bd-field-vehicle_number" />
                  </div>
                )}
                {(next.key === "dispatch" || next.key === "report" || next.key === "unload") && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={actionForm.date || today} onChange={setA("date")} data-testid="bd-field-date" />
                    </div>
                    {next.key === "unload" && (
                      <div>
                        <Label>Additional cost</Label>
                        <Input value={actionForm.additional_cost || ""} onChange={setA("additional_cost")} data-testid="bd-field-additional_cost" />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <PermButton entity="LogisticsBooking" action="UPDATE" variant="outline" size="sm"
                    onClick={() => setActionOpen(false)} disabled={acting}>
                    Cancel
                  </PermButton>
                  <PermButton entity="LogisticsBooking" action="UPDATE" size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={runAction} disabled={acting} data-testid="bd-action-submit-btn">
                    {acting ? "Saving…" : "Confirm"}
                  </PermButton>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {booking?.status === "DRAFT" && (
            <PermButton entity="LogisticsBooking" action="UPDATE" variant="outline"
              onClick={() => onEdit(booking)} data-testid="booking-detail-edit-btn">
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </PermButton>
          )}
          {booking && booking.status !== "DELETED" && (
            <PermButton entity="LogisticsBooking" action="DELETE" variant="outline"
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              onClick={() => onDelete(booking)} data-testid="booking-detail-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </PermButton>
          )}
          {next && !actionOpen && (
            <PermButton entity="LogisticsBooking" action="UPDATE"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={openAction} data-testid="booking-detail-next-action-btn">
              {next.label} <ArrowRight className="h-4 w-4 ml-1" />
            </PermButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
